<template>
    <footer class="footer px-0 pb-10 bg-cover" :style="footerBG">
        <div class="lg:flex w-full max-w-xl mx-auto px-4 md:px-16 items-start">
            <div class="lg:flex flex-1">
                <a
                    :href="footerOptions.footerLogoLink"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="mb-8 lg:mb-0 pr-8 flex lg:justify-end lg:border-r self-stretch"
                >
                    <img
                        v-if="footerLogo"
                        :src="footerLogo"
                        alt
                        class="block footer__logo"
                    />
                </a>

                <div class="flex flex-col lg:pl-6 xl:pl-10 justify-center">
                    <div v-html="mainFooterContent" class="text-white"></div>
                    <div v-if="isSuperUser">
                        <router-link to="/admin/">Admin Pages</router-link>
                    </div>
                </div>
            </div>
            <div
                class="lg:w-1/5 flex flex-col justify-top md:items-end my-8 lg:my-0"
            >
                <ul
                    class="flex flex-row justify-center gap-2"
                    v-if="socialData.length"
                >
                    <li v-for="social in socialData" :key="social.network">
                        <a
                            :href="social.url"
                            target="_blank"
                            rel="noopener noreferrer"
                            :aria-label="social.prettyName"
                            class="rounded-full shadow-inner bg-white text-body-text duration-500 transition-transform transform hover:scale-125 inline-flex"
                        >
                            <component
                                :is="social.component"
                                class="w-8 h-8 p-1"
                            />
                        </a>
                    </li>
                </ul>
                <button
                    @click.prevent="logout"
                    class="md:w-1/3 lg:w-2/5 button hover:border-twitter text-body-text block mt-2 ml-0 rounded-lg"
                >
                    Logout
                </button>
            </div>
        </div>

        <!--
            Bump `lastUpdateDate` as needed.
            * This will only show in dev enviornment.
            * It will be used as a reference point for testers to make sure they have the expected version of code.
        -->
        <div
            class="lg:flex w-full max-w-xl mx-auto px-4 md:px-16 mt-10 justify-end"
            aria-hidden="true"
            v-if="false"
        >
            <o-notification
                variant="info"
                class="text-right"
                has-icon
                :closable="false"
            >
                <b class="block mb-0">Last update</b>
                <div>{{ lastUpdateDate }}</div>
            </o-notification>
        </div>
    </footer>
</template>

<script lang="ts">
import store from "@/store";
import Vue from "vue";
import svgTwitter from "@/components/svg/svg-twitter.vue";
import svgLinkedin from "@/components/svg/svg-linkedin.vue";
import svgInstagram from "@/components/svg/svg-instagram.vue";
import svgFeedBurner from "@/components/svg/svg-feedburner.vue";
import svgYoutube from "@/components/svg/svg-youtube.vue";
import svgFacebook from "@/components/svg/svg-facebook.vue";
import svgPinterest from "@/components/svg/svg-pinterest.vue";

import { ConfigPageOptionsFooter } from "@/types/interfaces";

interface SocialMediaAccount {
    facebook?: string;
    twitter?: string;
    linkedin?: string;
    instagram?: string;
    youtube?: string;
    feedburner?: string;
    pinterest?: string;
}

interface SocialData {
    network: string;
    prettyName: string;
    component: string;
    url: string;
}

export default Vue.extend({
    data() {
        return {
            publicPath: process.env.BASE_URL,

            /**
             * Intentionally a hard coded string.
             *
             * Syntax:
             * month-day-year / (number to bump / reset if date is changed)
             * example: `01-01-21 / 2`
             *
             */
            lastUpdateDate: `07-19-21 / 3`
        };
    },
    components: {
        "svg-twitter": svgTwitter,
        "svg-linkedin": svgLinkedin,
        "svg-instagram": svgInstagram,
        "svg-feedburner": svgFeedBurner,
        "svg-youtube": svgYoutube,
        "svg-facebook": svgFacebook,
        "svg-pinterest": svgPinterest
    },
    // computed
    computed: {
        isProduction(): boolean {
            return store.getters.isProduction;
        },

        footerOptions(): ConfigPageOptionsFooter {
            return store.getters.getPageOptions("footer");
        },

        mainFooterContent(): string {
            return this.footerOptions?.content?.mainContent
                ? this.footerOptions?.content?.mainContent
                : "";
        },

        socialMediaAccount(): SocialMediaAccount {
            return store.getters.socialMediaAccount;
        },

        socialData(): Array<SocialData> {
            return Object.keys(this.socialMediaAccount)
                .filter((key) => {
                    const realKey = key as keyof SocialMediaAccount;
                    return (
                        this.socialMediaAccount[realKey] &&
                        this.socialMediaAccount[realKey] != ""
                    );
                })
                .map((key) => {
                    const realKey = key as keyof SocialMediaAccount;

                    let prettyName =
                        realKey.charAt(0).toUpperCase() + realKey.slice(1);

                    if (realKey === "feedburner") {
                        prettyName = "Feed Burner";
                    } else if (realKey === "youtube") {
                        prettyName = "YouTube";
                    } else if (realKey === "linkedin") {
                        prettyName = "LinkedIn";
                    }

                    return {
                        network: realKey,
                        prettyName,
                        component: `svg-${realKey}`,
                        url: this.socialMediaAccount[realKey] as string
                    };
                });
        },

        footerBG(): string {
            const footerImage = store.getters.layoutImages.footer || "";
            const path = `${this.publicPath}footer-images/`;
            let returnValue = "";

            if (footerImage) {
                returnValue = `background-image:url(${path}${footerImage})`;
            }

            return returnValue;
        },

        footerLogo(): string {
            return this.publicPath + "logos/" + store.getters.footerLogo;
        },

        isSuperUser(): boolean {
            return store.getters.isSuperUser;
        }
    },

    // lifecycle

    // Methods
    methods: {
        logout() {
            store.dispatch("logout");
        }
    }
});
</script>

<style lang="scss" scoped>
@import "../styles/components/footer";
</style>
